'use client';

import { useState, useEffect, useCallback } from 'react';

import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────

interface AssignedJob {
  job_id: string;
  address: string;
  status: string;
}

interface Employee {
  id: string;
  first_name: string;
  last_name: string;
  job_title: string;
  department: string;
  roles: string[];
  phone: string;
  email: string;
  avatar_url: string | null;
  hire_date: string;
  is_clocked_in: boolean;
  clocked_in_at: string | null;
  status: 'online' | 'offline' | 'away';
  assigned_jobs: AssignedJob[];
  monthly_sales_goal?: number | null;
  created_at: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const DEPARTMENTS = [
  'All Departments',
  'Project Management',
  'Quality Control',
  'Logistics',
  'Billing',
  'Warranty',
  'Operations',
  'Installers',
];

const ROLES = [
  'All Roles',
  'SuperAdmin',
  'Project_Manager',
  'Installer_Crew',
  'Executive',
  'Financial',
  'Operations',
  'Quality_Control',
  'Logistics',
  'Billing',
  'Expansion',
  'Warranty',
];

const DEPT_COLORS: Record<string, string> = {
  'Project Management': '#E89A3C',
  'Quality Control': '#8B5CF6',
  'Logistics': '#F59E0B',
  'Billing': '#10B981',
  'Warranty': '#EF4444',
  'Operations': '#1C2E5E',
  'Installers': '#EC4899',
};

const ROLE_COLORS: Record<string, string> = {
  SuperAdmin: '#1C2E5E',
  Project_Manager: '#E89A3C',
  Installer_Crew: '#EC4899',
  Executive: '#1C2E5E',
  Financial: '#10B981',
  Operations: '#1C2E5E',
  Quality_Control: '#8B5CF6',
  Logistics: '#F59E0B',
  Billing: '#10B981',
  Expansion: '#6366F1',
  Warranty: '#EF4444',
};

// ─── Mock fallback ────────────────────────────────────────────────────────────

const MOCK_EMPLOYEES: Employee[] = [];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getInitials(first: string, last: string) {
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

function getDeptColor(dept: string) {
  return DEPT_COLORS[dept] || '#6B7280';
}

function getRoleColor(role: string) {
  return ROLE_COLORS[role] || '#6B7280';
}

function formatClockInTime(clocked_in_at: string | null): string {
  if (!clocked_in_at) return '';
  const d = new Date(clocked_in_at);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

function formatHireDate(hire_date: string): string {
  if (!hire_date) return '';
  const d = new Date(hire_date);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getStatusJobLabel(status: string): string {
  const map: Record<string, string> = {
    in_progress: 'In Progress',
    scheduled: 'Scheduled',
    inspection_pending: 'Inspection Pending',
    delivery_scheduled: 'Delivery Scheduled',
    claim_filed: 'Claim Filed',
  };
  return map[status] || status;
}

// ─── Employee Card ────────────────────────────────────────────────────────────

interface EmployeeCardProps {
  employee: Employee;
  isOnline: boolean;
  onClick: () => void;
}

function EmployeeCard({ employee, isOnline, onClick }: EmployeeCardProps) {
  const initials = getInitials(employee.first_name, employee.last_name);
  const deptColor = getDeptColor(employee.department);
  // Presence-driven: green when the employee currently has the CRM open (logged in).
  const statusColor = isOnline ? '#10B981' : '#9CA3AF';

  return (
    <div
      onClick={onClick}
      className="rounded-xl p-4 cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5 border"
      style={{ background: '#FFFFFF', borderColor: '#F0F0F0', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
    >
      {/* Avatar + Status */}
      <div className="flex items-start justify-between mb-3">
        <div className="relative">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-sm"
            style={{ background: `linear-gradient(135deg, ${deptColor} 0%, ${deptColor}99 100%)` }}
          >
            {initials}
          </div>
          <span
            className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white"
            style={{ background: statusColor }}
          />
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); }}
          className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-all"
          title="Send message"
        >
          <Icon name="ChatBubbleLeftEllipsisIcon" size={15} className="text-gray-400" />
        </button>
      </div>

      {/* Name & Title */}
      <div className="mb-2">
        <p className="text-sm font-bold leading-tight" style={{ color: '#1C2E5E' }}>
          {employee.first_name} {employee.last_name}
        </p>
        <p className="text-xs text-gray-500 mt-0.5 truncate">{employee.job_title}</p>
      </div>

      {/* Department badge */}
      <div className="mb-2">
        <span
          className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
          style={{ background: `${deptColor}18`, color: deptColor }}
        >
          {employee.department}
        </span>
      </div>

      {/* Role pills */}
      <div className="flex flex-wrap gap-1 mb-3">
        {employee.roles?.slice(0, 2).map((role) => (
          <span
            key={role}
            className="text-[9px] font-semibold px-1.5 py-0.5 rounded"
            style={{ background: `${getRoleColor(role)}15`, color: getRoleColor(role) }}
          >
            {role.replace(/_/g, ' ')}
          </span>
        ))}
        {(employee.roles?.length || 0) > 2 && (
          <span className="text-[9px] text-gray-400">+{employee.roles.length - 2}</span>
        )}
      </div>

      {/* Contact */}
      <div className="space-y-1 mb-3">
        <div className="flex items-center gap-1.5">
          <Icon name="PhoneIcon" size={11} className="text-gray-400 flex-shrink-0" />
          <span className="text-[10px] text-gray-500 truncate">{employee.phone}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Icon name="EnvelopeIcon" size={11} className="text-gray-400 flex-shrink-0" />
          <span className="text-[10px] text-gray-500 truncate">{employee.email}</span>
        </div>
      </div>

      {/* Clock-in status */}
      <div
        className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5"
        style={{ background: employee.is_clocked_in ? 'rgba(16,185,129,0.08)' : 'rgba(156,163,175,0.1)' }}
      >
        <Icon
          name="ClockIcon"
          size={11}
          style={{ color: employee.is_clocked_in ? '#10B981' : '#9CA3AF' }}
        />
        <span
          className="text-[10px] font-semibold"
          style={{ color: employee.is_clocked_in ? '#10B981' : '#9CA3AF' }}
        >
          {employee.is_clocked_in
            ? `Clocked in ${formatClockInTime(employee.clocked_in_at)}`
            : 'Not clocked in'}
        </span>
      </div>
    </div>
  );
}

// ─── Slide-out Drawer ─────────────────────────────────────────────────────────

interface DrawerProps {
  employee: Employee | null;
  onClose: () => void;
}

function SalesGoalSection({ employee }: { employee: Employee }) {
  const supabase = createClient();
  const [goal, setGoal] = useState<string>(employee.monthly_sales_goal != null ? String(employee.monthly_sales_goal) : '');
  const [savedGoal, setSavedGoal] = useState<number | null>(employee.monthly_sales_goal ?? null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const fmt = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

  const save = async () => {
    setSaving(true);
    try {
      const val = goal.trim() === '' ? null : Number(goal);
      const { error } = await supabase.from('employees').update({ monthly_sales_goal: val }).eq('id', employee.id);
      if (!error) { setSavedGoal(val); setEditing(false); }
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-xl p-4" style={{ background: '#F8F9FA', border: '1px solid #F0F0F0' }}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400">Monthly Sales Goal</h3>
        {!editing && (
          <button onClick={() => setEditing(true)} className="text-gray-400 hover:text-gray-600" aria-label="Edit sales goal"><Icon name="PencilSquareIcon" size={14} /></button>
        )}
      </div>
      {editing ? (
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
            <input type="number" min="0" step="500" value={goal} onChange={(e) => setGoal(e.target.value)} placeholder="150000" autoFocus
              className="w-full pl-6 pr-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#E89A3C]/30" />
          </div>
          <button onClick={save} disabled={saving} className="px-3 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50" style={{ background: '#1C2E5E' }}>{saving ? '…' : 'Save'}</button>
          <button onClick={() => { setGoal(savedGoal != null ? String(savedGoal) : ''); setEditing(false); }} className="px-2 py-2 text-sm text-gray-400 hover:text-gray-600">Cancel</button>
        </div>
      ) : savedGoal != null && savedGoal > 0 ? (
        <p className="text-2xl font-black" style={{ color: '#10B981' }}>{fmt(savedGoal)}<span className="text-xs font-medium text-gray-400"> / month</span></p>
      ) : (
        <button onClick={() => setEditing(true)} className="text-sm font-medium text-gray-400 hover:text-gray-600">Not set — tap to set a target</button>
      )}
    </div>
  );
}

function EmployeeDrawer({ employee, onClose }: DrawerProps) {
  if (!employee) return null;

  const initials = getInitials(employee.first_name, employee.last_name);
  const deptColor = getDeptColor(employee.department);
  const statusColor = employee.status === 'online' ? '#10B981' : employee.status === 'away' ? '#F59E0B' : '#9CA3AF';
  const statusLabel = employee.status === 'online' ? 'Online' : employee.status === 'away' ? 'Away' : 'Offline';

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40"
        onClick={onClose}
      />
      {/* Drawer */}
      <div
        className="fixed top-0 right-0 h-full w-full max-w-md z-50 flex flex-col overflow-hidden"
        style={{ background: '#FFFFFF', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)' }}
      >
        {/* Drawer Header */}
        <div
          className="flex items-center justify-between px-5 py-4 flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #1C2E5E 0%, #1C2E5E 100%)' }}
        >
          <div className="flex items-center gap-3">
            <div className="relative">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold"
                style={{ background: `linear-gradient(135deg, ${deptColor} 0%, ${deptColor}99 100%)` }}
              >
                {initials}
              </div>
              <span
                className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white"
                style={{ background: statusColor }}
              />
            </div>
            <div>
              <h2 className="text-white font-bold text-base">
                {employee.first_name} {employee.last_name}
              </h2>
              <p className="text-white/70 text-xs">{employee.job_title}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-all"
          >
            <Icon name="XMarkIcon" size={18} />
          </button>
        </div>

        {/* Drawer Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">

          {/* Status + Department */}
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full"
              style={{ background: `${statusColor}18`, color: statusColor }}
            >
              <span className="w-2 h-2 rounded-full" style={{ background: statusColor }} />
              {statusLabel}
            </span>
            <span
              className="text-xs font-semibold px-2.5 py-1 rounded-full"
              style={{ background: `${deptColor}18`, color: deptColor }}
            >
              {employee.department}
            </span>
            {employee.is_clocked_in && (
              <span className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: 'rgba(16,185,129,0.1)', color: '#10B981' }}>
                <Icon name="ClockIcon" size={11} />
                Clocked in {formatClockInTime(employee.clocked_in_at)}
              </span>
            )}
          </div>

          {/* Contact Details */}
          <div
            className="rounded-xl p-4"
            style={{ background: '#F8F9FA', border: '1px solid #F0F0F0' }}
          >
            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">Contact Information</h3>
            <div className="space-y-2.5">
              <div className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(232,154,60,0.1)' }}>
                  <Icon name="PhoneIcon" size={13} style={{ color: '#E89A3C' }} />
                </div>
                <div>
                  <p className="text-[10px] text-gray-400">Phone</p>
                  <p className="text-sm font-semibold" style={{ color: '#1C2E5E' }}>{employee.phone}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(232,154,60,0.1)' }}>
                  <Icon name="EnvelopeIcon" size={13} style={{ color: '#E89A3C' }} />
                </div>
                <div>
                  <p className="text-[10px] text-gray-400">Email</p>
                  <p className="text-sm font-semibold truncate" style={{ color: '#1C2E5E' }}>{employee.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(232,154,60,0.1)' }}>
                  <Icon name="CalendarDaysIcon" size={13} style={{ color: '#E89A3C' }} />
                </div>
                <div>
                  <p className="text-[10px] text-gray-400">Hire Date</p>
                  <p className="text-sm font-semibold" style={{ color: '#1C2E5E' }}>{formatHireDate(employee.hire_date)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Assigned Roles */}
          <div
            className="rounded-xl p-4"
            style={{ background: '#F8F9FA', border: '1px solid #F0F0F0' }}
          >
            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">Assigned Roles</h3>
            <div className="flex flex-wrap gap-2">
              {employee.roles?.map((role) => (
                <span
                  key={role}
                  className="text-xs font-semibold px-2.5 py-1 rounded-lg"
                  style={{ background: `${getRoleColor(role)}15`, color: getRoleColor(role), border: `1px solid ${getRoleColor(role)}30` }}
                >
                  {role.replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          </div>

          {/* Performance Summary */}
          <div
            className="rounded-xl p-4"
            style={{ background: '#F8F9FA', border: '1px solid #F0F0F0' }}
          >
            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">Performance Summary</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="text-center rounded-lg p-2.5" style={{ background: 'rgba(232,154,60,0.06)', border: '1px solid rgba(232,154,60,0.15)' }}>
                <p className="text-lg font-black" style={{ color: '#E89A3C' }}>98%</p>
                <p className="text-[9px] text-gray-500 font-medium">Attendance</p>
              </div>
              <div className="text-center rounded-lg p-2.5" style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)' }}>
                <p className="text-lg font-black" style={{ color: '#10B981' }}>4.8</p>
                <p className="text-[9px] text-gray-500 font-medium">Rating</p>
              </div>
              <div className="text-center rounded-lg p-2.5" style={{ background: 'rgba(3,52,148,0.06)', border: '1px solid rgba(3,52,148,0.15)' }}>
                <p className="text-lg font-black" style={{ color: '#1C2E5E' }}>{employee.assigned_jobs?.length || 0}</p>
                <p className="text-[9px] text-gray-500 font-medium">Active Jobs</p>
              </div>
            </div>
          </div>

          {/* Sales Goal */}
          <SalesGoalSection employee={employee} />

          {/* Active Job Assignments */}
          {(employee.assigned_jobs?.length || 0) > 0 && (
            <div
              className="rounded-xl p-4"
              style={{ background: '#F8F9FA', border: '1px solid #F0F0F0' }}
            >
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">Active Job Assignments</h3>
              <div className="space-y-2">
                {employee.assigned_jobs?.map((job, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-3 rounded-lg px-3 py-2.5"
                    style={{ background: '#FFFFFF', border: '1px solid #E5E7EB' }}
                  >
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(3,52,148,0.1)' }}>
                      <Icon name="BriefcaseIcon" size={13} style={{ color: '#1C2E5E' }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold font-mono" style={{ color: '#1C2E5E' }}>{job.job_id}</p>
                      <p className="text-[10px] text-gray-500 truncate">{job.address}</p>
                    </div>
                    <span
                      className="text-[9px] font-semibold px-1.5 py-0.5 rounded flex-shrink-0"
                      style={{ background: 'rgba(232,154,60,0.1)', color: '#E89A3C' }}
                    >
                      {getStatusJobLabel(job.status)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Drawer Footer */}
        <div className="flex-shrink-0 px-5 py-4 border-t border-gray-100 flex gap-3">
          <button
            className="flex-1 py-2.5 rounded-xl text-white text-sm font-bold transition-all hover:opacity-90 active:scale-95"
            style={{ background: '#1C2E5E' }}
          >
            Edit Profile
          </button>
          <button
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all hover:bg-gray-100 active:scale-95 border"
            style={{ color: '#E89A3C', borderColor: '#E89A3C' }}
          >
            <Icon name="ChatBubbleLeftEllipsisIcon" size={15} />
            Message
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

// ─── Add Employee Modal ───────────────────────────────────────────────────────

function AddEmployeeModal({ onClose }: { onClose: () => void }) {
  const supabase = createClient();
  const [form, setForm] = useState({
    first_name: '', last_name: '', job_title: '',
    department: 'Operations', email: '', phone: '', hire_date: '', monthly_sales_goal: '',
  });
  const [roles, setRoles] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.first_name.trim() || !form.last_name.trim() || !form.job_title.trim()) {
      setError('First name, last name, and job title are required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // Array-form (no .single()) after write — see CLAUDE.md.
      const { error: err } = await supabase.from('employees').insert({
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        job_title: form.job_title.trim(),
        department: form.department,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        hire_date: form.hire_date || null,
        monthly_sales_goal: form.monthly_sales_goal ? Number(form.monthly_sales_goal) : null,
        roles,
      });
      if (err) throw err;
      onClose(); // realtime INSERT subscription adds the card
    } catch (e: any) {
      setError(e?.message ?? 'Failed to add employee.');
      setSaving(false);
    }
  };

  const input = 'w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#E89A3C]/30 focus:border-[#E89A3C]';

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-bold" style={{ color: '#1C2E5E' }}>Add Employee</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><Icon name="XMarkIcon" size={20} /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          {error && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-600">{error}</div>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">First Name *</label>
              <input className={input} value={form.first_name} onChange={(e) => set('first_name', e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Last Name *</label>
              <input className={input} value={form.last_name} onChange={(e) => set('last_name', e.target.value)} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Job Title *</label>
            <input className={input} value={form.job_title} onChange={(e) => set('job_title', e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Department</label>
              <select className={input} value={form.department} onChange={(e) => set('department', e.target.value)}>
                {DEPARTMENTS.slice(1).map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Hire Date</label>
              <input type="date" className={input} value={form.hire_date} onChange={(e) => set('hire_date', e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Work Email</label>
              <input className={input} value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="user@getnextroof.com" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Work Phone</label>
              <input className={input} value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="(937) 555-0100" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Monthly Sales Goal ($)</label>
            <input type="number" min="0" step="500" className={input} value={form.monthly_sales_goal} onChange={(e) => set('monthly_sales_goal', e.target.value)} placeholder="e.g. 150000" />
            <p className="text-[11px] text-gray-400 mt-1">Shows as their target on the Sales Dashboard.</p>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Roles</label>
            <div className="flex gap-2">
              {['Admin', 'Manager', 'Sales'].map((r) => {
                const on = roles.includes(r);
                return (
                  <button key={r} type="button"
                    onClick={() => setRoles((prev) => on ? prev.filter((x) => x !== r) : [...prev, r])}
                    className={`px-3 py-1.5 rounded-lg text-sm font-semibold border ${on ? 'text-white border-transparent' : 'text-gray-600 border-gray-200 bg-white'}`}
                    style={on ? { background: '#1C2E5E' } : {}}>
                    {r}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-100">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="px-5 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50 hover:opacity-90"
            style={{ background: '#E89A3C' }}>
            {saving ? 'Adding…' : 'Add Employee'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function EmployeeDirectoryInteractive() {
  const { currentUser } = useAuth();
  const supabase = createClient();

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDept, setSelectedDept] = useState('All Departments');
  const [selectedRole, setSelectedRole] = useState('All Roles');
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);

  // ── Fetch employees ──────────────────────────────────────────────────────────
  const fetchEmployees = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('employees')
        .select('*')
        .order('department', { ascending: true })
        .order('first_name', { ascending: true });

      if (error) {
        setEmployees(MOCK_EMPLOYEES);
      } else {
        setEmployees((data as Employee[]) || MOCK_EMPLOYEES);
      }
    } catch {
      setEmployees(MOCK_EMPLOYEES);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  // ── Real-time subscription ───────────────────────────────────────────────────
  useEffect(() => {
    const channel = supabase
      .channel('employees_realtime')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'employees' },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            setEmployees((prev) => [...prev, payload.new as Employee]);
          } else if (payload.eventType === 'UPDATE') {
            setEmployees((prev) =>
              prev.map((e) => (e.id === payload.new.id ? (payload.new as Employee) : e))
            );
            // Update drawer if open
            setSelectedEmployee((prev) =>
              prev?.id === payload.new.id ? (payload.new as Employee) : prev
            );
          } else if (payload.eventType === 'DELETE') {
            setEmployees((prev) => prev.filter((e) => e.id !== payload.old.id));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // ── Live presence: who currently has the CRM open (logged in) ────────────────
  // AppLayout tracks each authenticated user on the shared 'online-users'
  // channel; here we read the roster and mark employees online by email.
  const [onlineEmails, setOnlineEmails] = useState<Set<string>>(new Set());
  useEffect(() => {
    const channel = supabase.channel('online-users');
    const syncPresence = () => {
      const state = channel.presenceState() as Record<string, Array<{ email?: string }>>;
      const emails = new Set<string>();
      Object.values(state).forEach((arr) =>
        arr.forEach((p) => { if (p.email) emails.add(p.email.toLowerCase()); })
      );
      setOnlineEmails(emails);
    };
    channel
      .on('presence', { event: 'sync' }, syncPresence)
      .on('presence', { event: 'join' }, syncPresence)
      .on('presence', { event: 'leave' }, syncPresence)
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  const isEmployeeOnline = (emp: Employee): boolean =>
    !!emp.email && onlineEmails.has(emp.email.toLowerCase());

  // ── Filtered employees ───────────────────────────────────────────────────────
  const filteredEmployees = employees.filter((emp) => {
    const q = searchQuery.toLowerCase();
    const matchesSearch =
      !q ||
      `${emp.first_name} ${emp.last_name}`.toLowerCase().includes(q) ||
      emp.job_title?.toLowerCase().includes(q) ||
      emp.email?.toLowerCase().includes(q) ||
      emp.department?.toLowerCase().includes(q);

    const matchesDept = selectedDept === 'All Departments' || emp.department === selectedDept;
    const matchesRole = selectedRole === 'All Roles' || emp.roles?.includes(selectedRole);

    return matchesSearch && matchesDept && matchesRole;
  });

  // ── Metrics ──────────────────────────────────────────────────────────────────
  const totalEmployees = employees.length;
  const onLeaveCount = employees.filter((e) => e.status === 'away').length;
  const openPositions = 0; // static for now

  // ── Grouped by department ────────────────────────────────────────────────────
  // Known departments first (in list order); anyone with a blank or unlisted
  // department falls into an "Other" bucket so no employee is silently dropped.
  const knownDepts = new Set(DEPARTMENTS.slice(1));
  const departmentGroups = DEPARTMENTS.slice(1).reduce<Record<string, Employee[]>>((acc, dept) => {
    const deptEmployees = filteredEmployees.filter((e) => e.department === dept);
    if (deptEmployees.length > 0) {
      acc[dept] = deptEmployees;
    }
    return acc;
  }, {});
  const otherEmployees = filteredEmployees.filter((e) => !e.department || !knownDepts.has(e.department));
  if (otherEmployees.length > 0) {
    departmentGroups['Other'] = otherEmployees;
  }

  return (
    <div className="min-h-screen" style={{ background: '#F8F9FA' }}>

      {/* ── Sticky Header ──────────────────────────────────────────────────────── */}
      <div
        className="sticky top-[60px] z-30 flex items-center justify-between px-6 py-3.5 border-b"
        style={{ background: '#FFFFFF', borderColor: '#F0F0F0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}
      >
        <h1 className="text-xl font-black" style={{ color: '#1C2E5E' }}>Employee Directory</h1>
        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative hidden sm:block">
            <Icon name="MagnifyingGlassIcon" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search employees..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 pr-4 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:border-[#E89A3C] transition-all w-56"
              style={{ background: '#F8F9FA' }}
            />
          </div>
          {/* Add Employee */}
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-bold transition-all hover:opacity-90 active:scale-95"
            style={{ background: '#E89A3C' }}
          >
            <Icon name="PlusIcon" size={15} />
            <span className="hidden sm:inline">Add Employee</span>
          </button>
          {/* Avatar */}
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #E89A3C 0%, #1C2E5E 100%)' }}
          >
            {getInitials(currentUser.first_name, currentUser.last_name)}
          </div>
        </div>
      </div>

      <div className="px-6 py-6 space-y-6">

        {/* ── Metric Cards ──────────────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            {
              label: 'Total Employees',
              value: loading ? '—' : totalEmployees,
              icon: 'UsersIcon',
              color: '#1C2E5E',
              bg: 'rgba(0,1,80,0.06)',
            },
            {
              label: 'On Leave / Away',
              value: loading ? '—' : onLeaveCount,
              icon: 'CalendarDaysIcon',
              color: '#F59E0B',
              bg: 'rgba(245,158,11,0.08)',
            },
            {
              label: 'Open Positions',
              value: openPositions,
              icon: 'BriefcaseIcon',
              color: '#E89A3C',
              bg: 'rgba(232,154,60,0.08)',
            },
          ].map((card) => (
            <div
              key={card.label}
              className="rounded-xl p-4 flex items-center gap-4"
              style={{ background: '#FFFFFF', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', border: '1px solid #F0F0F0' }}
            >
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: card.bg }}
              >
                <Icon name={card.icon as any} size={20} style={{ color: card.color }} />
              </div>
              <div>
                <div className="flex items-center gap-1.5">
                  <p className="text-2xl font-black" style={{ color: '#1C2E5E' }}>{card.value}</p>
                </div>
                <p className="text-xs text-gray-500 font-medium">{card.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ── Filter Bar ────────────────────────────────────────────────────────── */}
        <div
          className="rounded-xl px-4 py-3 flex flex-wrap items-center gap-3"
          style={{ background: '#FFFFFF', border: '1px solid #F0F0F0', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
        >
          <Icon name="FunnelIcon" size={15} className="text-gray-400 flex-shrink-0" />
          <span className="text-xs font-semibold text-gray-500">Filter:</span>

          {/* Department */}
          <select
            value={selectedDept}
            onChange={(e) => setSelectedDept(e.target.value)}
            className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-[#E89A3C] bg-white"
            style={{ color: '#1C2E5E' }}
          >
            {DEPARTMENTS.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>

          {/* Role */}
          <select
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-[#E89A3C] bg-white"
            style={{ color: '#1C2E5E' }}
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
            ))}
          </select>

          {/* Mobile search */}
          <div className="relative sm:hidden flex-1 min-w-[140px]">
            <Icon name="MagnifyingGlassIcon" size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-7 pr-3 py-1.5 text-xs rounded-lg border border-gray-200 focus:outline-none focus:border-[#E89A3C] w-full"
              style={{ background: '#F8F9FA' }}
            />
          </div>

          {/* Results count */}
          <span className="ml-auto text-xs text-gray-400 font-medium">
            {filteredEmployees.length} employee{filteredEmployees.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* ── Department Sections ───────────────────────────────────────────────── */}
        {loading ? (
          <div className="space-y-6">
            {[1, 2, 3].map((i) => (
              <div key={i}>
                <div className="h-8 w-48 rounded-lg animate-pulse mb-4" style={{ background: '#E5E7EB' }} />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {[1, 2, 3, 4].map((j) => (
                    <div key={j} className="h-52 rounded-xl animate-pulse" style={{ background: '#E5E7EB' }} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : Object.keys(departmentGroups).length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ background: 'rgba(232,154,60,0.1)' }}>
              <Icon name="UsersIcon" size={28} style={{ color: '#E89A3C' }} />
            </div>
            <p className="text-lg font-bold" style={{ color: '#1C2E5E' }}>No employees found</p>
            <p className="text-sm text-gray-500 mt-1">Try adjusting your filters or search query</p>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(departmentGroups).map(([dept, deptEmployees]) => {
              const deptColor = getDeptColor(dept);
              return (
                <div key={dept}>
                  {/* Section Header */}
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className="w-1 h-6 rounded-full flex-shrink-0"
                      style={{ background: deptColor }}
                    />
                    <h2 className="text-base font-black" style={{ color: '#1C2E5E' }}>{dept}</h2>
                    <span
                      className="text-xs font-bold px-2.5 py-0.5 rounded-full"
                      style={{ background: `${deptColor}18`, color: deptColor }}
                    >
                      {deptEmployees.length} {deptEmployees.length === 1 ? 'employee' : 'employees'}
                    </span>
                    <div className="flex-1 h-px" style={{ background: '#F0F0F0' }} />
                  </div>

                  {/* Employee Cards Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {deptEmployees.map((emp) => (
                      <EmployeeCard
                        key={emp.id}
                        employee={emp}
                        isOnline={isEmployeeOnline(emp)}
                        onClick={() => setSelectedEmployee(emp)}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Slide-out Drawer ──────────────────────────────────────────────────── */}
      {selectedEmployee && (
        <EmployeeDrawer
          employee={selectedEmployee}
          onClose={() => setSelectedEmployee(null)}
        />
      )}

      {/* ── Add Employee Modal ────────────────────────────────────────────────── */}
      {showAddModal && <AddEmployeeModal onClose={() => setShowAddModal(false)} />}
    </div>
  );
}
